import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';

import Provision from '../../../../../api/provision';
import Table from '../../../../../components/Table';
import useApiQuery from '../../../../../hooks/useApiQuery';

import type { ErrorType } from '@/types';
import type { BareMetalCluster, Product } from '@/types/productTypes';
import type { TableRowData } from '@/types/tableTypes';

export interface BareMetalClustersProps {
  setActiveResources: (hasActive: boolean) => void;
  product: Product;
}

const BareMetalClusters = ({
  setActiveResources,
  product,
}: BareMetalClustersProps): React.JSX.Element => {
  const navigate = useNavigate();
  const {
    data: clusters = [],
    error: fetchError,
    makeApiCall: fetchClusters,
  } = useApiQuery<BareMetalCluster[]>(Provision.fetchBareMetalClustersV1);

  useEffect(() => {
    if (product.details.juiceGroup) {
      fetchClusters(product.details.juiceGroup);
    }
  }, [fetchClusters, product.details.juiceGroup]);

  useEffect(() => {
    if (clusters.length) {
      setActiveResources(
        clusters.some(cluster => ['Pending', 'Approved', 'Provisioned'].includes(cluster.status))
      );
    }
  }, [clusters, setActiveResources]);

  const fields = [
    { id: 'name', title: 'Name' },
    { id: 'group', title: 'Group' },
    { id: 'cloud', title: 'Cloud' },
    { id: 'created', title: 'Created At' },
    { id: 'last_updated', title: 'Last Updated' },
    { id: 'device_count', title: 'Device Count' },
  ];

  const rows: TableRowData[] = clusters.map((cluster: BareMetalCluster) => ({
    key: `${cluster.name}-${cluster.group.name}-${cluster.cloud.name}`,
    onRowClick: () => navigate(`/products/${product._id}/caas-clusters/${cluster.name}`),
    data: [
      { field: 'name', value: cluster.name },
      { field: 'group', value: cluster.group.name },
      { field: 'cloud', value: cluster.cloud.name },
      { field: 'created', value: cluster.created },
      { field: 'last_updated', value: cluster.last_updated },
      { field: 'device_count', value: cluster.device_count },
    ],
  }));

  return (
    <Table
      fields={fields}
      rows={rows}
      error={fetchError as ErrorType}
    />
  );
};

export default BareMetalClusters;
